import React from "react";
import { FlaskConical } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	BETA_FEATURES,
	BetaFeature,
	useBetaFeatureEnabled,
	useSetBetaFeature,
} from "@/hooks/betaFeatures";
import { useCurrentUser } from "@/networking/user.hooks";
import { SettingsLeaf } from "./SettingsLeafLayout";

interface ToggleProps {
	checked: boolean;
	disabled?: boolean;
	label: string;
	onChange: (checked: boolean) => void;
}

/** Switch-styled on/off control — a beta flag is either on or off, never a form field. */
const Toggle: React.FC<ToggleProps> = ({ checked, disabled, label, onChange }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		disabled={disabled}
		onClick={() => onChange(!checked)}
		className={cn(
			"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-solid border-transparent transition-colors",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2",
			checked ? "bg-brand" : "bg-gray-300",
			disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
		)}
	>
		<span
			aria-hidden="true"
			className={cn(
				"inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
				checked ? "translate-x-[22px]" : "translate-x-[2px]"
			)}
		/>
	</button>
);

/** One feature in the list: what it is, and a switch for it. */
const BetaFeatureRow: React.FC<{ feature: BetaFeature; disabled?: boolean }> = ({
	feature,
	disabled,
}) => {
	const enabled = useBetaFeatureEnabled(feature.key);
	const setBetaFeature = useSetBetaFeature();

	return (
		<div className="flex items-start justify-between gap-6 rounded-lg border border-solid border-gray-200 bg-white p-4">
			<div className="min-w-0">
				<div className="text-sm font-medium text-gray-900">{feature.label}</div>
				<p className="mt-1 text-sm leading-snug text-gray-500">{feature.description}</p>
			</div>
			<Toggle
				checked={enabled}
				disabled={disabled}
				label={feature.label}
				onChange={(next) => setBetaFeature(feature.key, next)}
			/>
		</div>
	);
};

/**
 * "Beta-funktionalitet" — per-user opt-in to features that are still being
 * built. The choice lives on the logged-in user's own profile
 * (`profile.preferences.betaFeatures`), not on the organisation and not in this
 * browser, so it follows the user between devices and affects nobody else. Any
 * dashboard user may flip these, and the flag is the only thing gating the
 * feature — no org flag, no role scope — so switching one on gives the user the
 * real feature.
 */
const BetaFunktionalitetPage = () => {
	const { isLoading } = useCurrentUser();

	return (
		<SettingsLeaf
			title={
				<span className="inline-flex items-center gap-2">
					<FlaskConical className="h-5 w-5 text-gray-400" />
					Beta-funktionalitet
				</span>
			}
			description="Slå på funktioner som fortfarande är under utveckling. Inställningen gäller bara ditt eget konto och följer med dig mellan datorer — den påverkar inte dina kollegor. Beta-funktioner kan ändras eller tas bort utan förvarning."
		>
			<div className="space-y-3">
				{BETA_FEATURES.map((feature) => (
					<BetaFeatureRow key={feature.key} feature={feature} disabled={isLoading} />
				))}
			</div>
		</SettingsLeaf>
	);
};

export default BetaFunktionalitetPage;
